<template>
  <div class="tache-mapping-container">
    <div class="page-header mb-4">
      <h1><i class="fas fa-exchange-alt"></i> Correspondances Tâches</h1>
      <p class="text-muted">Définir la correspondance entre les identifiants SMD et les noms dans la table des tâches.</p>
    </div>

    <!-- Paramètre de visibilité -->
    <b-card class="mb-4">
      <template #header>
        <h5 class="mb-0"><i class="fas fa-eye"></i> Visibilité des temps pour les clients</h5>
      </template>
      <div class="d-flex align-items-center">
        <b-form-checkbox v-model="showTempsToClients" switch @change="saveSettings" :disabled="savingSettings">
          {{ showTempsToClients ? 'Les clients peuvent voir les temps passés sur leurs tickets' : 'Les temps sont masqués pour les clients' }}
        </b-form-checkbox>
        <b-spinner v-if="savingSettings" small class="ml-2"></b-spinner>
      </div>
    </b-card>

    <b-row>
      <!-- Mapping Clients -->
      <b-col md="6">
        <b-card class="mb-4">
          <template #header>
            <h5 class="mb-0"><i class="fas fa-building"></i> Clients</h5>
          </template>

          <!-- Formulaire ajout/édition -->
          <b-form @submit.prevent="saveClientMapping" class="mb-4">
            <b-form-group label="Client SMD *">
              <b-form-select v-model="clientForm.smd_client_uid" :options="clientOptions" required @change="onClientSelect">
                <template #first><b-form-select-option value="" disabled>-- Sélectionner un client --</b-form-select-option></template>
              </b-form-select>
              <b-form-text v-if="loadingClients"><i class="fas fa-spinner fa-spin"></i> Chargement...</b-form-text>
            </b-form-group>
            <b-form-group label="Valeur dans la table tâche *">
              <b-form-input v-model="clientForm.tache_client" placeholder="ex: SMD" required></b-form-input>
              <b-form-text>Ce texte sera écrit dans la colonne <code>client</code> de la table tâche</b-form-text>
            </b-form-group>
            <div class="d-flex">
              <b-button type="submit" variant="primary" size="sm" :disabled="savingClient">
                <i v-if="savingClient" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-save"></i>
                {{ clientForm.uid ? 'Mettre à jour' : 'Ajouter' }}
              </b-button>
              <b-button v-if="clientForm.uid" type="button" variant="outline-secondary" size="sm" class="ml-2" @click="resetClientForm">
                Annuler
              </b-button>
            </div>
          </b-form>

          <hr />

          <!-- Liste -->
          <div v-if="loadingMapping" class="text-center"><b-spinner small></b-spinner></div>
          <div v-else-if="clientMappings.length === 0" class="text-muted text-center">Aucune correspondance définie</div>
          <b-table v-else :items="clientMappings" :fields="clientFields" small striped hover>
            <template #cell(actions)="data">
              <b-button size="sm" variant="outline-secondary" class="mr-1" @click="editClientMapping(data.item)">
                <i class="fas fa-edit"></i>
              </b-button>
              <b-button size="sm" variant="outline-danger" @click="deleteClientMapping(data.item.uid)">
                <i class="fas fa-trash"></i>
              </b-button>
            </template>
          </b-table>
        </b-card>
      </b-col>

      <!-- Mapping Utilisateurs -->
      <b-col md="6">
        <b-card class="mb-4">
          <template #header>
            <h5 class="mb-0"><i class="fas fa-user"></i> Utilisateurs</h5>
          </template>

          <b-alert show variant="info" class="mb-3">
            <i class="fas fa-info-circle"></i>
            Ces correspondances servent aussi à l'assignation des tickets : pour chaque collaborateur,
            seul le compte marqué <strong>Principal</strong> apparaît dans le menu « Assigné à ». Les autres
            comptes du même collaborateur (ex: jeremie_1, jeremie_2) utilisent automatiquement l'identité
            du compte principal pour la gestion des tickets (création, assignation, suivi, lu/non lu).
          </b-alert>

          <!-- Formulaire ajout/édition -->
          <b-form @submit.prevent="saveUserMapping" class="mb-4">
            <b-form-group label="Utilisateur SMD *">
              <b-form-select v-model="userForm.smd_user_uid" :options="userOptions" required @change="onUserSelect">
                <template #first><b-form-select-option value="" disabled>-- Sélectionner un utilisateur --</b-form-select-option></template>
              </b-form-select>
              <b-form-text v-if="loadingUsers"><i class="fas fa-spinner fa-spin"></i> Chargement...</b-form-text>
              <b-form-text v-else>Plusieurs comptes (jeremie_1, jeremie_2…) peuvent pointer vers le même collaborateur</b-form-text>
            </b-form-group>
            <b-form-group label="Nom complet dans la table tâche *">
              <b-form-input v-model="userForm.tache_collaborateur" placeholder="ex: Jérémie" required></b-form-input>
              <b-form-text>Ce texte sera écrit dans la colonne <code>collaborateur</code> de la table tâche</b-form-text>
            </b-form-group>
            <b-form-group>
              <b-form-checkbox v-model="userForm.principal" switch>
                Compte principal
              </b-form-checkbox>
              <b-form-text>Le compte principal du collaborateur est celui utilisé pour les tickets et affiché dans « Assigné à ». Un seul principal par collaborateur.</b-form-text>
            </b-form-group>
            <div class="d-flex">
              <b-button type="submit" variant="primary" size="sm" :disabled="savingUser">
                <i v-if="savingUser" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-save"></i>
                {{ userForm.uid ? 'Mettre à jour' : 'Ajouter' }}
              </b-button>
              <b-button v-if="userForm.uid" type="button" variant="outline-secondary" size="sm" class="ml-2" @click="resetUserForm">
                Annuler
              </b-button>
            </div>
          </b-form>

          <hr />

          <!-- Liste -->
          <div v-if="loadingMapping" class="text-center"><b-spinner small></b-spinner></div>
          <div v-else-if="userMappings.length === 0" class="text-muted text-center">Aucune correspondance définie</div>
          <b-table v-else :items="userMappings" :fields="userFields" small striped hover>
            <template #cell(principal)="data">
              <b-badge v-if="data.item.principal" variant="success"><i class="fas fa-check"></i> Principal</b-badge>
            </template>
            <template #cell(actions)="data">
              <b-button size="sm" variant="outline-secondary" class="mr-1" @click="editUserMapping(data.item)">
                <i class="fas fa-edit"></i>
              </b-button>
              <b-button size="sm" variant="outline-danger" @click="deleteUserMapping(data.item.uid)">
                <i class="fas fa-trash"></i>
              </b-button>
            </template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import axios from 'axios';
import tacheService from '../services/tache';

export default {
  name: 'TacheMapping',
  data() {
    return {
      loadingMapping: false,
      loadingClients: false,
      loadingUsers: false,
      savingSettings: false,
      savingClient: false,
      savingUser: false,
      showTempsToClients: false,
      clientMappings: [],
      userMappings: [],
      smdClients: [],
      smdUsers: [],
      clientForm: { uid: null, smd_client_uid: '', smd_client_nom: '', tache_client: '' },
      userForm: { uid: null, smd_user_uid: '', smd_user_nom: '', tache_collaborateur: '', principal: false },
      clientFields: [
        { key: 'smd_client_nom', label: 'Client SMD' },
        { key: 'tache_client', label: 'Valeur tâche' },
        { key: 'actions', label: '' }
      ],
      userFields: [
        { key: 'smd_user_nom', label: 'Utilisateur SMD' },
        { key: 'tache_collaborateur', label: 'Collaborateur tâche' },
        { key: 'principal', label: 'Principal' },
        { key: 'actions', label: '' }
      ]
    };
  },
  computed: {
    isAdmin() {
      const user = this.$store.state.user;
      return !!(user && user.details && user.details.client_uid);
    },
    clientOptions() {
      return this.smdClients.map(c => ({ value: c.uid, text: c.nom || c.uid }));
    },
    userOptions() {
      return this.smdUsers.map(u => ({ value: u.uid, text: `${u.prenom || ''} ${u.nom || ''} (${u.identifiant})`.trim() }));
    }
  },

  mounted() {
    if (!this.isAdmin) {
      this.$router.push({ name: 'Home' });
      return;
    }
    this.loadAll();
    this.loadSmdClients();
    this.loadSmdUsers();
  },
  methods: {
    async loadSmdClients() {
      this.loadingClients = true;
      try {
        const jwt = this.$store.state.jwt;
        const r = await axios.get(`${process.env.VUE_APP_SAAS_BACK}/clients/all`, { headers: { Authorization: `Bearer ${jwt}` } });
        this.smdClients = (r.data.clients || []).sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
      } catch (e) {
        console.error('Error loading clients:', e);
      } finally {
        this.loadingClients = false;
      }
    },

    async loadSmdUsers() {
      this.loadingUsers = true;
      try {
        const jwt = this.$store.state.jwt;
        const r = await axios.get(`${process.env.VUE_APP_SAAS_BACK}/smd_users/list`, { headers: { Authorization: `Bearer ${jwt}` } });
        this.smdUsers = (r.data.users || []).sort((a, b) => (a.identifiant || '').localeCompare(b.identifiant || ''));
      } catch (e) {
        console.error('Error loading users:', e);
      } finally {
        this.loadingUsers = false;
      }
    },

    async loadAll() {
      this.loadingMapping = true;
      try {
        const [mapping, settings] = await Promise.all([
          tacheService.getMapping(),
          tacheService.getSettings()
        ]);
        this.clientMappings = mapping.client_mappings || [];
        this.userMappings = mapping.user_mappings || [];
        this.showTempsToClients = !!(settings.settings && settings.settings.show_temps_to_clients);
      } catch (error) {
        console.error('Error loading mapping:', error);
        this.$notify({ group: 'info', title: 'Erreur', text: 'Erreur lors du chargement', type: 'error' });
      } finally {
        this.loadingMapping = false;
      }
    },

    async saveSettings() {
      this.savingSettings = true;
      try {
        await tacheService.saveSettings({ show_temps_to_clients: this.showTempsToClients });
        this.$notify({ group: 'info', title: 'Succès', text: 'Paramètre enregistré', type: 'success' });
      } catch (error) {
        this.$notify({ group: 'info', title: 'Erreur', text: 'Erreur lors de l\'enregistrement', type: 'error' });
      } finally {
        this.savingSettings = false;
      }
    },

    async saveClientMapping() {
      this.savingClient = true;
      try {
        await tacheService.saveClientMapping(this.clientForm);
        this.$notify({ group: 'info', title: 'Succès', text: 'Correspondance client enregistrée', type: 'success' });
        this.resetClientForm();
        this.loadAll();
      } catch (error) {
        this.$notify({ group: 'info', title: 'Erreur', text: error.response && error.response.data && error.response.data.error || 'Erreur', type: 'error' });
      } finally {
        this.savingClient = false;
      }
    },

    editClientMapping(item) {
      this.clientForm = { ...item };
    },

    resetClientForm() {
      this.clientForm = { uid: null, smd_client_uid: '', smd_client_nom: '', tache_client: '' };
      // Restore smd_client_nom when selecting
    },

    onClientSelect(uid) {
      const c = this.smdClients.find(x => x.uid === uid);
      if (c) this.clientForm.smd_client_nom = c.nom || uid;
    },

    async deleteClientMapping(uid) {
      if (!confirm('Supprimer cette correspondance ?')) return;
      try {
        await tacheService.deleteClientMapping(uid);
        this.$notify({ group: 'info', title: 'Succès', text: 'Correspondance supprimée', type: 'success' });
        this.loadAll();
      } catch (error) {
        this.$notify({ group: 'info', title: 'Erreur', text: 'Erreur lors de la suppression', type: 'error' });
      }
    },

    async saveUserMapping() {
      this.savingUser = true;
      try {
        await tacheService.saveUserMapping(this.userForm);
        this.$notify({ group: 'info', title: 'Succès', text: 'Correspondance utilisateur enregistrée', type: 'success' });
        this.resetUserForm();
        this.loadAll();
      } catch (error) {
        this.$notify({ group: 'info', title: 'Erreur', text: error.response && error.response.data && error.response.data.error || 'Erreur', type: 'error' });
      } finally {
        this.savingUser = false;
      }
    },

    editUserMapping(item) {
      this.userForm = { ...item };
    },

    resetUserForm() {
      this.userForm = { uid: null, smd_user_uid: '', smd_user_nom: '', tache_collaborateur: '', principal: false };
    },

    onUserSelect(uid) {
      const u = this.smdUsers.find(x => x.uid === uid);
      if (u) this.userForm.smd_user_nom = `${u.prenom || ''} ${u.nom || ''} (${u.identifiant})`.trim();
    },

    async deleteUserMapping(uid) {
      if (!confirm('Supprimer cette correspondance ?')) return;
      try {
        await tacheService.deleteUserMapping(uid);
        this.$notify({ group: 'info', title: 'Succès', text: 'Correspondance supprimée', type: 'success' });
        this.loadAll();
      } catch (error) {
        this.$notify({ group: 'info', title: 'Erreur', text: 'Erreur lors de la suppression', type: 'error' });
      }
    }
  }
};
</script>

<style scoped>
.tache-mapping-container {
  padding: 20px;
}
.page-header h1 {
  font-size: 1.8rem;
}
</style>
